import { styled } from '@linaria/react';
import { useRef, useState } from 'react';
import { useSuggestedPrompts } from '../../../../api/agents-client/agent-client.hooks';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';

const VISIBLE_PROMPT_COUNT = 3;

interface FloraPromptChipsProps {
  onSelect: (prompt: string) => void;
}

interface PromptChipProps {
  prompt: string;
  onSelect: (prompt: string) => void;
}

/**
 * Renders the user's suggested prompts as pill chips — the first three, plus a
 * "+N more" chip that reveals the rest. Long prompts wrap to at most two lines
 * and end in an ellipsis, with the full prompt in a tooltip; the full prompt
 * is always what gets selected. Renders nothing until prompts load or when
 * there are none.
 *
 * @param {(prompt: string) => void} onSelect - Called with the chip's full prompt text; the caller decides whether to send it
 * @returns {JSX.Element | null} The chip row, or null when there are no prompts
 */
export function FloraPromptChips({ onSelect }: FloraPromptChipsProps) {
  const { data: suggestedPromptsData } = useSuggestedPrompts();
  const suggestedPrompts = suggestedPromptsData?.suggested_prompts ?? [];
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  const visiblePrompts = showAllPrompts ? suggestedPrompts : suggestedPrompts.slice(0, VISIBLE_PROMPT_COUNT);

  if (suggestedPrompts.length === 0) return null;

  return (
    <ChipsRow>
      {visiblePrompts.map((prompt) => (
        <PromptChip key={prompt} prompt={prompt} onSelect={onSelect} />
      ))}
      {!showAllPrompts && suggestedPrompts.length > VISIBLE_PROMPT_COUNT && (
        <MoreChip onClick={() => setShowAllPrompts(true)} type="button">
          +{suggestedPrompts.length - VISIBLE_PROMPT_COUNT} more
        </MoreChip>
      )}
    </ChipsRow>
  );
}

/**
 * One suggested-prompt chip, clamped to two lines. The full-prompt tooltip
 * opens only when the label is actually truncated — measured on hover/focus
 * rather than with a live observer, as chips rarely change size.
 *
 * @param {string} prompt - Full prompt text, shown (clamped) and passed to onSelect
 * @param {(prompt: string) => void} onSelect - Called with the full prompt on click
 * @returns {JSX.Element} The chip, wrapped in its tooltip
 */
function PromptChip({ prompt, onSelect }: PromptChipProps) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const openIfTruncated = () => {
    const label = labelRef.current;
    if (label && label.scrollHeight > label.clientHeight) setIsTooltipOpen(true);
  };
  const closeTooltip = () => setIsTooltipOpen(false);

  return (
    <Tooltip
      label={prompt}
      opened={isTooltipOpen}
      variant="black"
      position="top"
      withArrow
      multiline
      w={320}
      zIndex={600}
    >
      <Chip
        type="button"
        onClick={() => onSelect(prompt)}
        onMouseEnter={openIfTruncated}
        onMouseLeave={closeTooltip}
        onFocus={openIfTruncated}
        onBlur={closeTooltip}
      >
        <ChipLabel ref={labelRef}>{prompt}</ChipLabel>
      </Chip>
    </Tooltip>
  );
}

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

const Chip = styled.button`
  padding: 6px 14px;
  border: 1px solid var(--sky-light);
  /* Still a pill on one line; reads as a rounded card when it wraps. */
  border-radius: 16px;
  max-width: 100%;
  background: var(--base-white);
  font-size: 14px;
  line-height: 20px;
  color: var(--ink-base);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  font-family: var(--font-family);

  &:hover {
    border-color: var(--secondary-base);
    background: var(--secondary-lightest);
    color: var(--secondary-darkest);
  }

  [data-theme='dark'] & {
    &:hover {
      border-color: var(--secondary-base);
      background: var(--secondary-lightest);
      color: var(--ink-darkest);
    }
  }
`;

const ChipLabel = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
  text-align: center;
`;

const MoreChip = styled.button`
  padding: 6px 14px;
  border: 1px dashed var(--sky-light);
  border-radius: 20px;
  background: transparent;
  font-size: 14px;
  color: var(--ink-lighter);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  font-family: var(--font-family);
  white-space: nowrap;

  &:hover {
    border-color: var(--ink-lighter);
    color: var(--ink-base);
  }
`;
